/**
 * Trunnion AI - Execution List Page
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { Select } from '@/components/ui/Select';
import { useExecutionList } from '@/hooks/queries/useExecutions';
import { ROUTES } from '@/config/routes';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import type { Execution, ExecutionFilters } from '@/types';

export default function ExecutionListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ExecutionFilters>({
    page: 1,
    pageSize: 25,
  });

  const { data, isLoading } = useExecutionList(filters);

  const columns: Column<Execution>[] = [
    {
      key: 'workflow',
      header: 'Workflow',
      sortable: true,
      render: (e) => (
        <div>
          <span className="text-white font-medium">{e.workflowName}</span>
          <p className="text-xs text-trunnion-slate-400">v{e.workflowVersion}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (e) => {
        const config = EXECUTION_STATUS_CONFIG[e.status];
        return (
          <Badge
            variant={
              e.status === 'completed'
                ? 'success'
                : e.status === 'failed'
                  ? 'error'
                  : e.status === 'running'
                    ? 'info'
                    : e.status === 'waiting_approval'
                      ? 'purple'
                      : 'default'
            }
            dot
          >
            {config?.label ?? e.status}
          </Badge>
        );
      },
    },
    {
      key: 'classification',
      header: 'Level',
      render: (e) => <ClassificationTag level={e.classification} />,
    },
    {
      key: 'triggeredBy',
      header: 'Triggered By',
      render: (e) => (
        <span className="text-trunnion-slate-300">{e.triggeredBy}</span>
      ),
    },
    {
      key: 'tokens',
      header: 'Tokens',
      render: (e) => (
        <span className="text-trunnion-slate-300">
          {e.totalTokensUsed.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'started',
      header: 'Started',
      sortable: true,
      render: (e) => (
        <span className="text-xs text-trunnion-slate-300">
          {new Date(e.startedAt).toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Executions</h1>
        <p className="mt-1 text-sm text-trunnion-slate-400">
          Monitor and manage workflow executions.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Select
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'running', label: 'Running' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
            { value: 'waiting_approval', label: 'Awaiting Approval' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
          value={filters.status ?? ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              status: e.target.value
                ? (e.target.value as ExecutionFilters['status'])
                : undefined,
              page: 1,
            })
          }
        />
      </div>

      <DataTable<Execution>
        columns={columns}
        data={data?.executions ?? []}
        keyExtractor={(e) => e.id}
        loading={isLoading}
        emptyMessage="No executions found"
        page={filters.page ?? 1}
        pageSize={filters.pageSize ?? 25}
        total={data?.total}
        onPageChange={(page) => setFilters({ ...filters, page })}
        onRowClick={(e) => navigate(ROUTES.EXECUTION_DETAIL(e.id))}
      />
    </div>
  );
}
